<template>
  <div class=" flex flex-col bg-[#f8fafc] gap-6 overflow-hidden">
    <!-- Daily Transaction Selection/Header -->
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center shrink-0">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Daily Transaction</h2>
      </div>

      <form class="px-6 pb-5 pt-2 shrink-0" @submit.prevent>
        <div class="flex flex-wrap items-center gap-x-12 gap-y-4">
          <div class="flex items-center gap-3 min-w-[240px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">
              Calendar:
            </label>
            <input
              v-model="currentDate"
              type="date"
              :max="todayDate"
              @keydown.prevent
              @change="fetchTransactions"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
          </div>
          <div class="flex items-center gap-3 min-w-[280px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">
              Department:
            </label>
            <select
              v-model="selectedDepartment"
              @change="fetchTransactions"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select Department</option>
              <option v-for="dept in filteredDepartments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
            </select>
          </div>
        </div>
      </form>

      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <table v-else class="w-full daily-transaction-table">
          <thead>
            <tr>
              <th class="w-10 text-center sticky-col bg-[#f1f5f9]">No.</th>
              <th class="min-w-[150px] sticky-col left-[40px] bg-[#f1f5f9]">Menu Item</th>
              <th class="w-24">Sales (Man)</th>
              <th class="w-24">Sales (Sys)</th>
              <th class="w-24">Mgmt</th>
              <th class="w-24">Room</th>
              <th class="w-24">Promo</th>
              <th class="w-24">Co. Cost</th>
              <th class="w-24">Spoilage</th>
              <th class="w-24">Other</th>
              <th class="w-28 text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in transactions" :key="index" class="hover:bg-slate-50 transition-colors group">
              <td class="text-center sticky-col bg-white group-hover:bg-slate-50">{{ index + 1 }}</td>
              <td class="sticky-col left-[40px] bg-white group-hover:bg-slate-50">
                {{ row.item }}
              </td>
              <td>
                <input v-model.number="row.salesManual" type="number" :disabled="inventoryFieldDisabled(row, 'salesManual')" @input="calculateTotal(index)" :class="inventoryFieldDisabled(row, 'salesManual') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'" class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm">
              </td>
              <td>
                <input disabled v-model.number="row.salesSystem" type="number" class="w-full px-1.5 py-0.5 border border-transparent rounded outline-none bg-slate-50 text-slate-500 transition-all text-sm cursor-not-allowed">
              </td>
              <td>
                <input disabled v-model.number="row.management" type="number" class="w-full px-1.5 py-0.5 border border-transparent rounded outline-none bg-slate-50 text-slate-500 transition-all text-sm cursor-not-allowed">
              </td>
              <td>
                <input v-model.number="row.room" type="number" :disabled="inventoryFieldDisabled(row, 'room')" @input="calculateTotal(index)" :class="inventoryFieldDisabled(row, 'room') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'" class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm">
              </td>
              <td>
                <input v-model.number="row.promotion" type="number" :disabled="inventoryFieldDisabled(row, 'promotion')" @input="calculateTotal(index)" :class="inventoryFieldDisabled(row, 'promotion') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'" class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm">
              </td>
              <td>
                <input v-model.number="row.companyCost" type="number" :disabled="inventoryFieldDisabled(row, 'companyCost')" @input="calculateTotal(index)" :class="inventoryFieldDisabled(row, 'companyCost') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'" class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm">
              </td>
              <td>
                <input v-model.number="row.spoilage" type="number" :disabled="inventoryFieldDisabled(row, 'spoilage')" @input="calculateTotal(index)" :class="inventoryFieldDisabled(row, 'spoilage') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'" class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm">
              </td>
              <td>
                <input v-model.number="row.other" type="number" :disabled="inventoryFieldDisabled(row, 'other')" @input="calculateTotal(index)" :class="inventoryFieldDisabled(row, 'other') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'" class="w-full px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm">
              </td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ row.total }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="transactions.length > 0" class="flex justify-center mt-6">
          <button
            @click="openSaveModal"
            :disabled="!canSave || isSaving"
            class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <span v-if="isSaving" class="spinner"></span>
            {{ isSaving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Save confirmation modal -->
    <div v-if="saveModal.open" class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeSaveModal">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
        <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
          <div class="text-left">
            <h3 class="text-base font-bold m-0">Confirm Save Transaction</h3>
          </div>
          <button type="button" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeSaveModal" aria-label="Close save modal">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div class="p-5 space-y-4">
          <div class="text-xs font-semibold text-slate-500">Please enter the code below to confirm that you want to save.</div>

          <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
            {{ saveModal.code }}
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
            <input
              v-model.trim="saveModal.input"
              type="text"
              maxlength="4"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
            >
          </div>
          <div v-if="saveModal.error" class="text-xs text-red-600 font-semibold">{{ saveModal.error }}</div>
        </div>

        <div class="px-5 py-4 flex items-center justify-end gap-2">
          <button type="button" @click="closeSaveModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
          <button
            type="button"
            @click="confirmSave"
            :disabled="isSaving"
            class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
          >
            {{ isSaving ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { apiFetch } from '../../../utils/api'
import store from '../../../store'

const selectedDepartment = ref('')
const currentDate = ref('')
const todayDate = new Date().toISOString().split('T')[0]
const departments = ref([])
const transactions = ref([])
const loading = ref(false)
const isSaving = ref(false)
const isSnapshotLocked = ref(false)

function sortByMenuItemName(rows) {
  const arr = Array.isArray(rows) ? rows : []
  return arr.sort((a, b) =>
    String(a?.item || '').localeCompare(String(b?.item || ''), undefined, { sensitivity: 'base' })
  )
}

function applyPendingEditPermissions(rows, pendingList) {
  const list = Array.isArray(pendingList) ? pendingList : [];
  return rows.map((row) => {
    const rid = row.dailyTransactionId;
    const relevant = list.filter((p) => Number(p.dailyTransactionId) === Number(rid));
    return {
      ...row,
      canEditSalesManual: relevant.some((p) => p.salesManual === true),
      canEditManagement: relevant.some((p) => p.management === true),
      canEditRoom: relevant.some((p) => p.room === true),
      canEditPromotion: relevant.some((p) => p.promotion === true),
      canEditCompanyCost: relevant.some((p) => p.companyCost === true),
      canEditSpoilage: relevant.some((p) => p.spoilage === true),
      canEditOther: relevant.some((p) => p.other === true)
    };
  });
}

function inventoryFieldDisabled(row, field) {
  if (isSnapshotLocked.value) {
    if (field === 'salesManual') return !row.canEditSalesManual;
    if (field === 'management') return !row.canEditManagement;
    if (field === 'room') return !row.canEditRoom;
    if (field === 'promotion') return !row.canEditPromotion;
    if (field === 'companyCost') return !row.canEditCompanyCost;
    if (field === 'spoilage') return !row.canEditSpoilage;
    if (field === 'other') return !row.canEditOther;
    return true;
  }
  return false;
}

const canSave = computed(() => {
  if (isSaving.value) return false;
  if (!selectedDepartment.value || !currentDate.value) return false;
  if (transactions.value.length === 0) return false;
  
  if (isSnapshotLocked.value) {
    const hasPermission = transactions.value.some(row => 
       row.canEditSalesManual || 
       row.canEditManagement || 
       row.canEditRoom || 
       row.canEditPromotion || 
       row.canEditCompanyCost || 
       row.canEditSpoilage || 
       row.canEditOther
    );
    if (!hasPermission) return false;
  }
  return true;
});

const saveModal = ref({
  open: false,
  code: '',
  input: '',
  error: ''
})

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveModal() {
  if (!canSave.value) return
  saveModal.value.open = true
  saveModal.value.code = generateVerificationCode()
  saveModal.value.input = ''
  saveModal.value.error = ''
}

function closeSaveModal() {
  if (isSaving.value) return
  saveModal.value.open = false
}

async function confirmSave() {
  const expected = String(saveModal.value.code || '').trim()
  const entered = String(saveModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveModal.value.error = ''
  const ok = await saveAllRows()
  if (ok) {
    saveModal.value.open = false
  }
}

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

// All departments from token as names list
const userDeptList = computed(() => {
  const depts = currentUser.value?.departments || currentUser.value?.department?.name || currentUser.value?.departmentName || currentUser.value?.dept
  if (!depts) return []
  if (typeof depts === 'string') return depts.split(',').map(d => d.trim()).filter(Boolean)
  if (Array.isArray(depts)) return depts.filter(Boolean)
  return [depts].filter(Boolean)
})

const filteredDepartments = computed(() => {
  if (!userDeptList.value.length) return departments.value
  return departments.value.filter(d =>
    userDeptList.value.some(userDept => userDept.toLowerCase() === String(d.name || '').toLowerCase())
  )
})

watch(filteredDepartments, (newVal) => {
  if (newVal.length === 1 && !selectedDepartment.value) {
    selectedDepartment.value = newVal[0].id
    setTimeout(() => { fetchTransactions() }, 50)
  }
}, { deep: true })

async function fetchDepartments() {
  try {
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : (companyId.value ? `companyId=${companyId.value}` : '')

    const url = query ? `/departments?${query}` : '/departments'

    const res = await apiFetch(url)
    if (!res.ok) return
    const data = await res.json().catch(() => [])

    const raw = Array.isArray(data) ? data : []
    // Keep only active departments; userDeptList filter happens in filteredDepartments
    departments.value = raw.filter(d => {
      if (Number(d.status ?? 1) !== 1) return false
      const isCentral = d.isCentral ?? d.is_central
      return isCentral === 0 || isCentral === false
    })
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error fetching departments:', err)
  }
}

async function fetchTransactions() {
  if (!selectedDepartment.value || !currentDate.value) return
  loading.value = true
  try {
    const res = await apiFetch(`/daily-transactions?departmentId=${selectedDepartment.value}&calendar=${currentDate.value}`)
    if (res.ok) {
      const payload = await res.json().catch(() => ({ hasData: false, rows: [], pendingEditAccess: [] }))
      const rows = sortByMenuItemName(Array.isArray(payload?.rows) ? payload.rows : [])

      if (payload?.hasData) {
        isSnapshotLocked.value = true
        transactions.value = applyPendingEditPermissions(rows, payload?.pendingEditAccess || [])
      } else {
        // For no saved snapshot (including past dates), keep template editable.
        isSnapshotLocked.value = false
        transactions.value = rows
      }
    } else {
      console.error('Failed to fetch transactions')
    }
  } catch (err) {
    console.error('Error fetching transactions:', err)
  } finally {
    loading.value = false
  }
}

const calculateTotal = (index) => {
  const row = transactions.value[index]
  row.total = (Number(row.salesManual) || 0) + 
              (Number(row.salesSystem) || 0) + 
              (Number(row.management) || 0) + 
              (Number(row.room) || 0) + 
              (Number(row.promotion) || 0) + 
              (Number(row.companyCost) || 0) + 
              (Number(row.spoilage) || 0) + 
              (Number(row.other) || 0)
}

const saveAllRows = async () => {
  if (!canSave.value) return false

  isSaving.value = true
  try {
    const res = await apiFetch('/daily-transactions/batch', {
      method: 'POST',
      body: JSON.stringify({
        date: currentDate.value,
        calendar: currentDate.value,
        departmentId: selectedDepartment.value,
        items: transactions.value
      })
    })

    if (res.ok) {
      alert('Daily transactions saved successfully!')
      selectedDepartment.value = ''
      transactions.value = []
      return true
    } else {
      const err = await res.json()
      alert('Error saving: ' + (err.message || 'Unknown error'))
      return false
    }
  } catch (err) {
    console.error('Error saving transactions:', err)
    alert('Failed to save transactions.')
    return false
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  currentDate.value = ''
  fetchDepartments()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th,
td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.daily-transaction-table {
  width: 100%;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background-clip: padding-box;
}

th.sticky-col {
  z-index: 30;
}

/* Custom scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
